import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import RdaDrawer from '@/custolink-views/asset_frontend/redesigns/RdaDrawer'
import useUiContext from '@/custolink-hooks/useUiContext'
import { FrontendAssetScanApi } from '@/custolink-transpiled_by_server/bc.assets.api_methods'
import type { AssetViewDetails } from '@/custolink-views/asset_frontend/asset_view/useAssetViewFacade'
import AssetDrawerScope from '@/custolink-views/asset_frontend/redesigns/AssetDrawerScope'
import { PrimaryButton, SecondaryButton, TextField } from '@/custolink-views/asset_frontend/redesigns/formKit'

/**
 * The redesign's version of the classic `NudgeToDefaultViewDialog`: a category
 * view that wants a serial can offer the full asset page instead. Same
 * lookup, same navigation and the same "continue without a serial" escape, in
 * the design's bottom sheet rather than the classic centered dialog.
 */
export default function NudgeSerialSheet(p: {
  accent: string
  assetViewDetails: AssetViewDetails
  show: boolean
  onDenyNudge: () => void
}) {
  const [query, setQuery] = useState('')
  const uic = useUiContext()
  const { t } = useTranslation()
  const searchQuery = useMutation({
    mutationFn: () => FrontendAssetScanApi.assetLookup(uic._orgId, query.trim()),
  })

  useEffect(() => {
    if (searchQuery?.data?.defaultViewResult != null) {
      // Navigate to the asset landing page with the serial number
      window.location.href = `/asset/${searchQuery.data.defaultViewResult.serialnumber}`
    }
  }, [searchQuery?.data])

  return (
    <RdaDrawer
      title={t('nudge.unlockMore')}
      isOpen={p.show}
      placement="bottom"
      height="auto"
      onClose={p.onDenyNudge}
      onRequestClose={p.onDenyNudge}
    >
      <AssetDrawerScope fit accent={p.accent}>
        <div className="mx-auto flex w-full max-w-xl flex-col gap-4">
          <p className="text-sm leading-relaxed text-[var(--rda-muted)] sm:text-[15px]">
            {t('nudge.getDetailsAndSupport')}
          </p>

          <TextField
            placeholder={t('nudge.inputSerial')}
            aria-label={t('nudge.inputSerial')}
            value={query}
            className="rounded-lg sm:rounded-xl"
            onChange={(e) => setQuery(e.target.value)}
          />

          <PrimaryButton
            className="rounded-lg sm:rounded-xl"
            disabled={searchQuery.isPending}
            onClick={() => searchQuery.mutateAsync()}
          >
            {searchQuery.isPending ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : null}
            {t('nudge.unlockWithSerial')}
          </PrimaryButton>

          {searchQuery?.data != undefined && searchQuery.data.defaultViewResult == null ? (
            <p className="text-sm text-[var(--rda-muted)]">{t('nudge.search.noResults')}</p>
          ) : null}

          <SecondaryButton className="rounded-lg sm:rounded-xl" onClick={() => p.onDenyNudge()}>
            {t('nudge.continueWithoutSerial')}
          </SecondaryButton>
        </div>
      </AssetDrawerScope>
    </RdaDrawer>
  )
}
